import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { MplBottomSheet, MplButton } from '@mopla/ui';

import { formatEuroFromCents } from '../../operatorTickets/utils';

interface IOperatorTicketDetailBottomSheetProps {
	totalPriceInCents: number;
	onContinue: () => void;
	label?: string;
	variant?: 'outlined' | 'contained';
	isDisabled?: boolean;
	isLoading?: boolean;
	/**
	 * Subscription only — number of monthly periods. When set, the total renders
	 * as "{count} x {monthlyPrice}/Monat" (§9); otherwise a single total price.
	 */
	subscriptionPeriodCount?: number;
}

/**
 * Static (non-draggable) bottom sheet for the ticket detail page.
 * Mirrors the SubscribeDTicketBottomSheet pattern: MplBottomSheet renders
 * the sheet via MUI Drawer which pins to the viewport bottom and centers
 * inside the mobile-container max-width — no manual position:fixed needed.
 */
export const OperatorTicketDetailBottomSheet: FC<
	IOperatorTicketDetailBottomSheetProps
> = ({
	totalPriceInCents,
	onContinue,
	label,
	variant,
	isDisabled = false,
	isLoading = false,
	subscriptionPeriodCount,
}) => {
	const { t } = useTranslation('ticketshop');
	const ctaLabel = label ?? t('ticket_detail.pay_cta');
	// Keep the legacy behaviour when no explicit variant is passed.
	const ctaVariant = variant ?? (isDisabled ? 'contained' : 'outlined');

	return (
		<MplBottomSheet open isStatic testId="operator-ticket-detail-bottom-sheet">
			<Stack gap={2} sx={{ pt: 2, px: 2 }}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Typography variant="h5-sb" color="primary.main">
						{t('ticket_detail.total_label')}
					</Typography>
					{subscriptionPeriodCount && totalPriceInCents > 0 ? (
						<Stack direction="row" gap={0.5} alignItems="baseline">
							<Typography variant="b1-sb" color="solutions.600">
								{t('ticket_detail.total_subscription_prefix', {
									count: subscriptionPeriodCount,
								})}
							</Typography>
							<Typography variant="b1-sb" color="primary.main">
								{t('ticket_detail.total_subscription_amount', {
									price: formatEuroFromCents(totalPriceInCents),
								})}
							</Typography>
						</Stack>
					) : (
						<Typography
							variant="b1-sb"
							color={subscriptionPeriodCount ? 'solutions.600' : 'primary.main'}
						>
							{formatEuroFromCents(totalPriceInCents)}
						</Typography>
					)}
				</Stack>

				<MplButton
					variant={ctaVariant}
					color="primary"
					fullWidth
					onClick={onContinue}
					disabled={isDisabled}
					showLoader={isLoading}
					testId="operator-ticket-detail-continue-button"
				>
					{ctaLabel}
				</MplButton>
			</Stack>
		</MplBottomSheet>
	);
};
